import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const CONSTRUCTION_PROPOSAL_TEMPLATES = [
  "General Contracting Bid",
  "Commercial Build-Out Proposal",
  "Residential New Construction",
  "Renovation & Remodel Proposal",
  "Specialty Trade Bid Package",
];

export const CONSTRUCTION_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Construction Services Agreement",
    description: "General construction contract covering scope, schedule, payment terms, insurance, warranty, and change order procedures.",
    content: `CONSTRUCTION SERVICES AGREEMENT

This Construction Services Agreement ("Agreement") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

OWNER / CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Owner")

──────────────────────────────────────────────────

1. PROJECT DESCRIPTION

Project Name: {{project_description}}
Project Address: {{project_address}}

The Contractor agrees to furnish all labor, materials, equipment, and supervision necessary to complete the construction work described herein in accordance with the plans, specifications, and scope of work attached as Exhibit A.

──────────────────────────────────────────────────

2. SCOPE OF WORK

{{scope_of_work}}

The scope includes but is not limited to:
• Demolition and site preparation as required
• All structural, mechanical, electrical, and plumbing work per approved plans
• Finishing work including drywall, paint, flooring, and trim
• Final cleanup and punch list completion
• Obtaining all required inspections and certificates of occupancy

Excluded from scope (unless added via Change Order):
• Hazardous material abatement (asbestos, lead, mold)
• Landscaping and exterior hardscaping
• Owner-furnished fixtures, furniture, and equipment (FF&E)

──────────────────────────────────────────────────

3. PROJECT SCHEDULE

{{estimated_hours}}

Substantial Completion Date: ________________
Final Completion Date: ________________

Delays due to weather, force majeure, material shortages, or Owner-directed changes shall extend the completion date by an equivalent period. Contractor shall provide written notice of any anticipated delays within forty-eight (48) hours.

──────────────────────────────────────────────────

4. CONTRACT PRICE & PAYMENT

Total Contract Price: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment Schedule:
• 10% upon contract execution (mobilization deposit)
• Progress payments monthly based on percentage of completion
• 10% retainage held until final completion and punch list sign-off
• Final payment due within thirty (30) days of substantial completion

Late payments shall accrue interest at 1.5% per month. Contractor may suspend work if payment is more than fifteen (15) days past due.

──────────────────────────────────────────────────

5. CHANGE ORDERS

No changes to the scope, schedule, or contract price shall be made without a written Change Order signed by both parties. Change Orders shall include:
• Description of the changed work
• Cost impact (time and materials or lump sum)
• Schedule impact
• Overhead and profit markup (not to exceed {{overhead_markup}}%)

──────────────────────────────────────────────────

6. PERMITS & INSPECTIONS

A. Contractor shall obtain all necessary building permits and schedule all required inspections.
B. Permit costs are included in the contract price unless otherwise noted.
C. Owner shall provide timely access for all inspections.
D. Contractor shall maintain copies of all permits, inspection reports, and certificates on-site.

──────────────────────────────────────────────────

7. INSURANCE & BONDING

Contractor shall maintain the following minimum coverage:
• Commercial General Liability: $1,000,000 per occurrence / $2,000,000 aggregate
• Workers' Compensation: As required by state law
• Commercial Auto: $1,000,000 combined single limit
• Builder's Risk (if applicable): Full replacement value of the project

{{bond_requirements}}

Certificates of insurance shall be provided to Owner prior to commencement of work.

──────────────────────────────────────────────────

8. SAFETY & COMPLIANCE

A. Contractor shall comply with all applicable OSHA regulations and local building codes.
B. Contractor shall maintain a written safety plan and conduct regular safety meetings.
C. Contractor shall ensure all workers have required certifications and PPE.
D. Contractor is responsible for securing the job site at the end of each work day.
E. All subcontractors shall comply with the same safety standards.

──────────────────────────────────────────────────

9. WARRANTY

Contractor warrants all work performed under this Agreement for a period of one (1) year from the date of substantial completion. This warranty covers defects in materials and workmanship but does not cover:
• Normal wear and tear
• Damage caused by Owner misuse or negligence
• Items covered by manufacturer warranties

──────────────────────────────────────────────────

10. DISPUTE RESOLUTION

Any disputes arising under this Agreement shall first be submitted to mediation. If mediation fails, disputes shall be resolved by binding arbitration in accordance with the rules of the American Arbitration Association. The prevailing party shall be entitled to recover reasonable attorney fees.

──────────────────────────────────────────────────

11. TERMINATION

A. Owner may terminate for convenience with fourteen (14) days' written notice. Contractor shall be compensated for all work completed plus reasonable demobilization costs.
B. Either party may terminate for cause if the other party materially breaches this Agreement and fails to cure within ten (10) days of written notice.

──────────────────────────────────────────────────

12. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

13. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

OWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Subcontractor Agreement",
    description: "Agreement between general contractor and subcontractor covering trade-specific work, payment terms, insurance, and coordination requirements.",
    content: `SUBCONTRACTOR AGREEMENT

This Subcontractor Agreement ("Agreement") is entered into as of {{date}} by and between:

GENERAL CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "GC")

SUBCONTRACTOR:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
License #: ________________
(hereinafter referred to as "Subcontractor")

──────────────────────────────────────────────────

1. PROJECT & TRADE

Project: {{project_description}}
Project Address: {{project_address}}
Trade / Division: ________________

──────────────────────────────────────────────────

2. SCOPE OF SUBCONTRACT WORK

{{scope_of_work}}

Subcontractor shall furnish all labor, materials, tools, and equipment necessary to complete the trade work described above in accordance with the project plans, specifications, and GC's construction schedule.

──────────────────────────────────────────────────

3. SCHEDULE & COORDINATION

{{estimated_hours}}

Subcontractor shall coordinate with GC and other trades to avoid delays. Subcontractor shall provide a minimum of seventy-two (72) hours' notice before mobilizing to the site and shall attend all required coordination meetings.

──────────────────────────────────────────────────

4. COMPENSATION

Subcontract Price: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Progress payments issued within thirty (30) days of approved pay application
• 10% retainage held until final completion of subcontract work
• GC payment is contingent upon GC's receipt of payment from Owner (pay-when-paid)

──────────────────────────────────────────────────

5. INSURANCE REQUIREMENTS

Subcontractor shall maintain:
• Commercial General Liability: $1,000,000 per occurrence
• Workers' Compensation: Statutory limits
• Commercial Auto: $500,000 combined single limit

GC shall be named as additional insured on Subcontractor's CGL policy.

──────────────────────────────────────────────────

6. SAFETY & COMPLIANCE

Subcontractor shall comply with all OSHA regulations, site safety rules, and GC's safety program. All workers must complete site orientation before commencing work.

──────────────────────────────────────────────────

7. WARRANTY

Subcontractor warrants all work for one (1) year from the date of substantial completion of the overall project. Defects shall be corrected within seven (7) days of written notice.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

GENERAL CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

SUBCONTRACTOR:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Change Order Agreement",
    description: "Formal change order document for modifications to project scope, cost, or schedule during construction.",
    content: `CHANGE ORDER

Change Order #: ________
Date: {{date}}

Project: {{project_description}}
Project Address: {{project_address}}
Original Contract Date: ________________

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

OWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. DESCRIPTION OF CHANGE

{{scope_of_work}}

Reason for Change:
☐ Owner-Requested Change
☐ Unforeseen Site Condition
☐ Design Revision
☐ Code / Regulatory Requirement
☐ Value Engineering
☐ Other: ________________

──────────────────────────────────────────────────

2. COST IMPACT

Additional Cost: \${{estimated_cost}}
Credit (if applicable): $________________
Net Change: $________________

Breakdown:
• Labor: $________________
• Materials: $________________
• Equipment: $________________
• Subcontractor: $________________
• Overhead & Profit ({{overhead_markup}}%): $________________

──────────────────────────────────────────────────

3. SCHEDULE IMPACT

Schedule Extension: ________ calendar days
Revised Completion Date: ________________

──────────────────────────────────────────────────

4. CONTRACT ADJUSTMENT

Original Contract Price: $________________
Previous Change Orders: $________________
This Change Order: \${{estimated_cost}}
New Contract Price: $________________

──────────────────────────────────────────────────

5. ADDITIONAL NOTES

{{proposal_notes}}

──────────────────────────────────────────────────

6. AUTHORIZATION

By signing below, both parties agree to the changes described in this Change Order.

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

OWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Design-Build Agreement",
    description: "Combined design and construction contract for design-build delivery projects with integrated scope, fees, and responsibilities.",
    content: `DESIGN-BUILD AGREEMENT

This Design-Build Agreement ("Agreement") is entered into as of {{date}} by and between:

DESIGN-BUILDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Design-Builder")

OWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Owner")

──────────────────────────────────────────────────

1. PROJECT

Project Name: {{project_description}}
Project Address: {{project_address}}

──────────────────────────────────────────────────

2. DESIGN-BUILD SCOPE

The Design-Builder shall provide complete design and construction services including:

A. DESIGN PHASE:
• Schematic design and concept development
• Design development with engineering coordination
• Construction document preparation
• Permit application and approval

B. CONSTRUCTION PHASE:
• All construction as described in the scope of work
• Project management and site supervision
• Quality control and inspections
• Punch list and closeout

{{scope_of_work}}

──────────────────────────────────────────────────

3. PROJECT SCHEDULE

{{estimated_hours}}

Design Phase Duration: ________ weeks
Construction Phase Duration: ________ weeks
Total Project Duration: ________ weeks

──────────────────────────────────────────────────

4. COMPENSATION

Design Fee: $________________
Construction Cost: $________________
Total Design-Build Price: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

──────────────────────────────────────────────────

5. INSURANCE & WARRANTY

The Design-Builder shall maintain professional liability (E&O) insurance with a minimum limit of $1,000,000 in addition to standard construction insurance. All work warranted for one (1) year from substantial completion.

──────────────────────────────────────────────────

6. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

7. SIGNATURES

DESIGN-BUILDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

OWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];

export const CONSTRUCTION_TERMINOLOGY = {
  proposalLabel: "Construction Proposal",
  contractLabel: "Construction Agreement",
  staffLabel: "Crew / Trade Team",
  rateLabel: "Labor Rate",
  industryTerms: [
    "general contractor",
    "subcontractor",
    "change order",
    "punch list",
    "substantial completion",
    "retainage",
    "mobilization",
    "scope of work",
    "bill of quantities",
    "RFI",
    "submittal",
    "as-built drawings",
  ],
};
